/* eslint-disable @typescript-eslint/no-unused-vars */
import type { ShareAPIInterface } from './api-switcher'
import type { ShareType } from '../pages/ShareAlbum/permission_table'
import { client } from './client'
import { responseDataConverter } from './converters'
import {
  apiSharePhotoToPhoto,
  apiSharePhotosToPhotos,
  apiShareToShareWithLength,
  convertShareTypeToAPI
} from './share-converters'

export const ShareAPI: ShareAPIInterface = {
  listShares: (filter, signal) => {
    // Build query params only if they have values
    const queryParams: any = {}
    if (filter?.is_appendable !== undefined) {
      queryParams.appendable_only = filter.is_appendable
    }
    const convertedShareType = convertShareTypeToAPI(filter?.share_type)
    if (convertedShareType) {
      queryParams.share_type = convertedShareType
    }

    return responseDataConverter(
      client.GET('/shares', {
        params: Object.keys(queryParams).length > 0 ? { query: queryParams } : undefined,
        signal
      }),
      (shares) => shares.map(share => apiShareToShareWithLength(share))
    )
  },

  getShare: (share_id, password, signal) => responseDataConverter(
    client.GET('/share/{ShareId}', {
      params: {
        path: { ShareId: share_id },
        query: password ? { password } : undefined
      },
      signal
    }),
    (share) => {
      const shareData = apiShareToShareWithLength(share)
      return {
        ...shareData,
        is_readable: share.is_readable ?? true, // If we got the share, it's readable
        photos: apiSharePhotosToPhotos(share.photos)
      }
    }
  ),

  createShare: (name, photo_ids, share_type, auto_approve, password, is_sensitive) =>
    client.POST('/share', {
      body: {
        name,
        photo_ids,
        share_type: convertShareTypeToAPI(share_type),
        password: password || undefined,
        is_sensitive
      }
    }).then(async ({ data: share, error, response }) => {
      if (error || !share) {
        return {
          error: error ? { code: error.code || 0, message: error.message || 'Unknown error' } : { code: 0, message: 'No data returned' },
          response
        }
      }

      // Update share settings if needed (only auto_approve now, password is sent in POST)
      if (auto_approve !== undefined && auto_approve !== false) {
        await client.PATCH('/share/{ShareId}', {
          params: { path: { ShareId: share.album_id } },
          body: {
            auto_approve: auto_approve
          }
        })
      }

      const shareData = apiShareToShareWithLength(share)
      return {
        data: {
          ...shareData,
          photos: apiSharePhotosToPhotos(share.photos)
        },
        response
      }
    }),

  updateShare: (share_id, data) =>
    client.PATCH('/share/{ShareId}', {
      params: { path: { ShareId: share_id } },
      body: {
        name: data.name,
        // thumbnail_url is deprecated - use setShareThumbnail instead
        share_type: convertShareTypeToAPI(data.share_type),
        auto_approve: data.auto_approve,
        password: data.password,
        owner_id: data.owner_id,
        is_sensitive: data.is_sensitive
      }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  setShareThumbnail: (share_id, photo_id) =>
    client.PUT('/share/{ShareId}/thumbnail', {
      params: { path: { ShareId: share_id } },
      body: { photo_id }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  deleteShare: (share_id) =>
    client.DELETE('/share/{ShareId}', {
      params: { path: { ShareId: share_id } }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  leaveShare: (share_id) =>
    client.POST('/share/{ShareId}/leave', {
      params: { path: { ShareId: share_id } }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  appendPhotosToShare: (share_id, photo_ids) =>
    client.POST('/share/{ShareId}/photos', {
      params: { path: { ShareId: share_id } },
      body: { photo_ids }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  removePhotosFromShare: (share_id, photo_ids) =>
    client.DELETE('/share/{ShareId}/photos', {
      params: { path: { ShareId: share_id } },
      body: { photo_ids }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  getPhotoFromShare: (share_id, photo_id, password, signal) => responseDataConverter(
    client.GET('/share/{ShareId}/photo/{PhotoId}', {
      params: {
        path: { ShareId: share_id, PhotoId: photo_id },
        query: password ? { password } : undefined
      },
      signal
    }),
    apiSharePhotoToPhoto
  ),

  reactionToSharePhoto: (share_id, photo_id, reaction, add) =>
    responseDataConverter(
      client.POST('/share/{ShareId}/{PhotoId}/reaction', {
        params: {
          path: {
            ShareId: share_id,
            PhotoId: photo_id
          }
        },
        body: {
          share_id,
          action: add ? 'add' : 'remove',
          reaction
        }
      }),
      // The API returns just the reactions, we need to return a photo-like object
      (reactions) => ({
        photo_id,
        reactions,
        // These fields are required for the photo object but won't be used for updates
        taken_at: '',
        owner: {} as { user_id: string; user_name: string; metamory_id: string },
        in_users: [],
        in_users_candidate: [],
        in_vrchat_users_candidate: [],
        urls: {} as { thumbnail: string; original: string },
        width: 0,
        height: 0,
        bytes: 0,
        filename: '',
        favorite: false
      } as any)
    ),

  tagsToSharePhoto: (share_id, photo_id, in_user_ids) =>
    // TODO: No direct API endpoint for tagging in share context
    responseDataConverter(
      client.POST('/my/{PhotoId}/tag', {
        params: {
          path: { PhotoId: photo_id },
        },
        body: { in_user_ids },
      }),
      apiSharePhotoToPhoto
    ),

  reportSharePhoto: (share_id, photo_id) => {
    console.log('reportSharePhoto (not implemented)', share_id, photo_id)
    return Promise.resolve({
      error: {code: 501, message: 'Not Implemented'},
      response: new Response('', {status: 501})
    })
  },

  inviteUsersToShare: (share_id, user_ids) =>
    client.POST('/share/{ShareId}/invite', {
      params: { path: { ShareId: share_id } },
      body: { user_ids }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  approveUsersToShare: (share_id, user_ids) =>
    client.POST('/share/{ShareId}/join/accept', {
      params: { path: { ShareId: share_id } },
      body: { user_ids }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  acceptInvitationToShare: (share_id) =>
    client.POST('/share/{ShareId}/invite/accept', {
      params: { path: { ShareId: share_id } }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  joinRequestToShare: (share_id, _user_ids) =>
    client.POST('/share/{ShareId}/join', {
      params: { path: { ShareId: share_id } }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),


  kickUserFromShare: (share_id, user_id) =>
    client.DELETE('/share/{ShareId}/members', {
      params: {
        path: { ShareId: share_id }
      },
      body: { user_ids: [user_id] }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  cancelInvitationsToShare: (share_id: string, user_ids: string[]) =>
    Promise.all(
      user_ids.map(user_id =>
        client.DELETE('/share/{ShareId}/invite/{UserId}', {
          params: { path: { ShareId: share_id, UserId: user_id } }
        })
      )
    ).then((results) => {
      const failed = results.find(r => r.error)
      const response = results[results.length - 1]?.response ?? new Response()
      return failed?.error
        ? { error: { code: failed.error.code || 0, message: failed.error.message || 'Unknown error' }, response }
        : { response }
    }),

  acceptInvitation: (share_id) =>
    client.POST('/share/{ShareId}/invite/accept', {
      params: { path: { ShareId: share_id } }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  declineInvitation: (share_id) =>
    client.POST('/share/{ShareId}/invite/decline', {
      params: { path: { ShareId: share_id } }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  cancelInvitation: (share_id, user_id) =>
    client.DELETE('/share/{ShareId}/invite/{UserId}', {
      params: { path: { ShareId: share_id, UserId: user_id } }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

}
